import { getCompanySettings } from './api';
import { auth } from './firebase';
import { getDoc, doc } from 'firebase/firestore';
import { db } from './firebase';
import { API_CONFIG, getPaymentApiUrl } from './config';

export const NMI_API_URL = API_CONFIG.externalApis.nmiTransact;
export const NMI_COLLECT_JS_URL = API_CONFIG.externalApis.nmiCollectJs;

// Function to get NMI credentials for the current company
async function getNMICredentials(companyId?: string) {
  try {
    if (companyId) {
      // Use provided company ID (for public invoice payments)
      const settings = await getCompanySettings(companyId);
      if (!settings?.wikiPayitSettings?.tokenizationKey) {
        console.error(`NMI tokenization key not found for company ID: ${companyId}`);
        throw new Error('NMI tokenization key not configured');
      }
      
      return {
        tokenizationKey: settings.wikiPayitSettings.tokenizationKey
      };
    } else {
      // Use current user's company ID (for authenticated users)
      const user = auth.currentUser;
      if (!user?.uid) throw new Error('User not authenticated and no companyId provided');
      
      // Get the user's data to find their associated company
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (!userDoc.exists()) {
        throw new Error('User data not found');
      }
      
      const userData = userDoc.data();
      const userCompanyId = userData.companyId; // Use the companyId field from user document
      
      if (!userCompanyId) {
        console.error(`User ${user.uid} does not have a companyId in their user document`);
        throw new Error('User is not associated with a company');
      }
      
      // Now get settings using the actual company ID from user document
      const settings = await getCompanySettings(userCompanyId);
      if (!settings?.wikiPayitSettings?.tokenizationKey) {
        console.error(`NMI tokenization key not found for company ID: ${userCompanyId}`);
        throw new Error('NMI tokenization key not configured');
      }
      
      return {
        tokenizationKey: settings.wikiPayitSettings.tokenizationKey
      };
    }
  } catch (error) {
    console.error('Error getting NMI credentials:', error);
    throw error;
  }
}

export interface NMIPaymentParams {
  amount: number;
  orderId?: string;
  customerId?: string;
  customerVaultId?: string;
  orderDescription?: string;
  invoiceId?: string;
  companyId?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  savePaymentMethod?: boolean;
}

export interface NMIVaultParams {
  customerId: string;
  companyId: string;
  payment_token: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  address1?: string;
  city?: string;
  state?: string;
  zip?: string;
}

interface NMIResponse {
  success: boolean;
  transactionid?: string;
  transaction_id?: string;
  authcode?: string;
  responsetext?: string;
  response_code?: string;
  token?: string;
  error?: string;
  details?: string;
  cardLast4?: string;
  cardType?: string;
  expiryMonth?: string;
  expiryYear?: string;
  customerVaultId?: string;
  customer_vault_id?: string;
}

export class PaymentError extends Error {
  code: string;
  
  constructor(message: string, code: string) {
    super(message);
    this.code = code;
    this.name = 'PaymentError';
  }
}

// Store customer data without affecting CollectJS configuration
let customerData: Partial<NMIPaymentParams> = {};

// Function to initialize Collect.js
export const initializeCollectJS = async (companyId?: string): Promise<void> => {
  let retryCount = 0;
  const MAX_RETRIES = 3;
  const RETRY_DELAY = 2000; // 2 seconds

  const initializeWithRetry = async (): Promise<void> => {
    return new Promise((resolve, reject) => {
      try {
        // First, ensure any existing elements are removed
        const cleanup = () => {
          const existingScript = document.querySelector('script[src*="Collect.js"]');
          if (existingScript) {
            console.log('Removing existing Collect.js script');
            existingScript.remove();
          }
        };

        // Clean up existing elements
        cleanup();
        
        console.log(`Initializing CollectJS (attempt ${retryCount + 1}/${MAX_RETRIES})`);

        // Get NMI credentials and initialize
        getNMICredentials(companyId).then(credentials => {
          console.log('Got NMI credentials, tokenization key available:', !!credentials.tokenizationKey);
          
          // Load Collect.js script with tokenization key
          const script = document.createElement('script');
          script.src = NMI_COLLECT_JS_URL;
          script.setAttribute('data-tokenization-key', credentials.tokenizationKey);
          script.async = false;

          script.onload = () => {
            console.log('Collect.js script loaded, waiting for initialization');
            
            // Wait for CollectJS to be available
            let checkAttempts = 0;
            const MAX_CHECK_ATTEMPTS = 50;
            const checkCollectJS = setInterval(() => {
              checkAttempts++;
              if ((window as any).CollectJS) {
                clearInterval(checkCollectJS);
                try {
                  console.log('CollectJS object found, configuring...');
                  (window as any).CollectJS.configure({
                    variant: 'inline',
                    fields: {
                      ccnumber: {
                        selector: '#ccnumber',
                        title: 'Card Number',
                        placeholder: '•••• •••• •••• ••••',
                      },
                      ccexp: {
                        selector: '#ccexp',
                        title: 'Expiration',
                        placeholder: 'MM/YY',
                      },
                      cvv: {
                        selector: '#cvv',
                        title: 'CVV',
                        placeholder: 'CVV',
                      },
                    },
                    styleSniffer: false,
                    customCss: {
                      'background-color': '#ffffff',
                      'border': '1px solid #e5e7eb',
                      'border-radius': '0.375rem',
                      'padding': '0.5rem',
                      'font-size': '0.875rem',
                      'line-height': '1.25rem',
                      'width': '100%',
                    },
                    validCss: {
                      'border-color': '#059669',
                    },
                    invalidCss: {
                      'border-color': '#dc2626',
                    },
                  });
                  console.log('CollectJS configured successfully');
                  resolve();
                } catch (error) {
                  clearInterval(checkCollectJS);
                  console.error('Error configuring CollectJS:', error);
                  reject(error);
                }
              } else if (checkAttempts >= MAX_CHECK_ATTEMPTS) {
                clearInterval(checkCollectJS);
                console.error('CollectJS not available after maximum check attempts');
                reject(new Error('CollectJS initialization timed out'));
              }
            }, 100);
          };

          script.onerror = (e) => {
            console.error('Failed to load Collect.js script:', e);
            reject(new Error('Failed to load Collect.js'));
          };

          document.head.appendChild(script);
        }).catch(error => {
          console.error('Error getting NMI credentials:', error);
          reject(error);
        });
      } catch (error) {
        console.error('Error during CollectJS initialization:', error);
        reject(error);
      }
    });
  };

  // Main function with retry logic
  while (retryCount < MAX_RETRIES) {
    try {
      await initializeWithRetry();
      return; // Success, exit the function
    } catch (error) {
      retryCount++;
      console.error(`CollectJS initialization failed (attempt ${retryCount}/${MAX_RETRIES}):`, error);
      
      if (retryCount >= MAX_RETRIES) {
        console.error('Maximum retry attempts reached. CollectJS initialization failed.');
        throw error;
      }
      
      // Wait before retrying
      console.log(`Waiting ${RETRY_DELAY}ms before retrying...`);
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
    }
  }
};

// Function to configure CollectJS with payment fields and callback
export const configureCollectJS = (
  params: NMIPaymentParams, 
  onTokenSuccess: (token: string) => void,
  onTokenError?: (error: any) => void
) => {
  if (!(window as any).CollectJS) {
    throw new Error('Collect.js not initialized');
  }

  // Store customer data for later use
  customerData = {
    firstName: params.firstName || '',
    lastName: params.lastName || '',
    email: params.email || '',
    address: params.address || '',
    city: params.city || '',
    state: params.state || '',
    zip: params.zip || '',
  };

  try {
    (window as any).CollectJS.configure({
      variant: 'inline',
      callback: (response: any) => {
        console.log('CollectJS token response:', response);

        if (response.token) {
          // Extract card information from response
          const cardData = {
            token: response.token,
            last4: response.card?.number?.slice(-4) || '',
            cardType: response.card?.type || '',
            expiryMonth: '',
            expiryYear: ''
          };

          // Parse expiration date
          if (response.card?.exp) {
            const exp = response.card.exp;
            if (exp.length >= 4) {
              // Format is MMYY
              cardData.expiryMonth = exp.substring(0, 2);
              cardData.expiryYear = '20' + exp.substring(2);
            }
          }

          console.log('Card data extracted');

          // Store the card data for later use
          (window as any).__nmiCardData = cardData;
          
          onTokenSuccess(response.token);
        } else {
          console.error('Token generation failed:', response);
          if (onTokenError) {
            onTokenError(response);
          }
        }
      },
      validationCallback: (field: string, status: boolean, message: string) => {
        const element = document.getElementById(`${field}-error`);
        if (element) {
          element.textContent = status ? '' : message;
        }
      },
      fields: {
        ccnumber: {
          selector: '#ccnumber',
          title: 'Card Number',
          placeholder: '•••• •••• •••• ••••',
        },
        ccexp: {
          selector: '#ccexp',
          title: 'Expiration',
          placeholder: 'MM/YY',
        },
        cvv: {
          selector: '#cvv',
          title: 'CVV',
          placeholder: 'CVV',
        },
      },
      styleSniffer: false,
      customCss: {
        'background-color': '#ffffff',
        'border': '1px solid #e5e7eb',
        'border-radius': '0.375rem',
        'padding': '0.5rem',
        'font-size': '0.875rem',
        'line-height': '1.25rem',
        'width': '100%',
      },
      validCss: {
        'border-color': '#059669',
      },
      invalidCss: {
        'border-color': '#dc2626',
      },
    });
  } catch (error) {
    console.error('Error configuring CollectJS:', error);
    throw error;
  }
};

// Function to update customer data without affecting CollectJS
export const updateCustomerData = (params: Partial<NMIPaymentParams>) => {
  customerData = {
    ...customerData,
    ...params,
  };
};

// Function to create a customer in NMI vault
export const createVaultCustomer = async (params: NMIVaultParams): Promise<NMIResponse> => {
  try {
    // Use the backend API URL from config
    const apiUrl = getPaymentApiUrl('vault');
    
    console.log('Attempting to create vault customer for ID:', params.customerId);
    
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify(params)
    });

    // If response is not okay, handle different error cases
    if (!response.ok) {
      // Try to parse the error response as JSON
      try {
        const errorData = await response.json();
        
        // Special handling for duplicate customer errors
        if (errorData.details && (
          errorData.details.includes('Duplicate Customer Vault Id') || 
          errorData.code === '3'
        )) {
          console.log('Customer already exists in vault, using existing vault ID');
          // Return partial success with the customer vault ID that already exists
          return {
            success: true,
            customerVaultId: params.customerId ? `cust_${params.customerId.replace(/[^a-zA-Z0-9]/g, '')}` : undefined,
            responsetext: 'Using existing customer vault record'
          };
        }
        
        throw new PaymentError(
          errorData.error || 'Vault creation failed',
          errorData.code || 'VAULT_ERROR'
        );
      } catch (parseError) {
        // If we couldn't parse the response as JSON, throw a generic error
        console.error('Error parsing vault API response:', parseError);
        throw new PaymentError(
          'Invalid response from vault API',
          'VAULT_ERROR'
        );
      }
    }

    // Process successful response
    try {
      const responseData = await response.json();
      console.log('Vault creation processed successfully');
      return responseData;
    } catch (parseError) {
      console.error('Error parsing vault success response:', parseError);
      throw new PaymentError(
        'Invalid response format from payment server',
        'VAULT_ERROR'
      );
    }
  } catch (error) {
    // Special handling for network or connection errors
    if (error instanceof Error && error.message.includes('Failed to fetch')) {
      console.error('Network error when creating vault customer:', error);
      // Generate a fallback customer vault ID based on customer ID
      const fallbackVaultId = params.customerId ? 
        `cust_${params.customerId.replace(/[^a-zA-Z0-9]/g, '')}` : undefined;
      
      if (fallbackVaultId) {
        console.log('Using fallback vault ID:', fallbackVaultId);
        return {
          success: true,
          customerVaultId: fallbackVaultId,
          responsetext: 'Using fallback customer vault ID due to API error'
        };
      }
    }
    
    // Handle PaymentError cases
    if (error instanceof PaymentError) {
      throw error;
    }
    
    // Handle all other error types
    throw new PaymentError(
      error instanceof Error ? error.message : 'Vault creation failed',
      'VAULT_ERROR'
    );
  }
};

// Modified process payment function to use stored customer data
export const processPaymentWithToken = async (token: string, params: NMIPaymentParams): Promise<NMIResponse> => {
  const cardData = (window as any).__nmiCardData || {};
  const paymentToken = token;
  let currentCompanyId = '';

  try {
    // Get company ID from params or current user
    if (params.companyId) {
      currentCompanyId = params.companyId;
    } else {
      const user = auth.currentUser;
      if (!user?.uid) throw new Error('User not authenticated and no companyId provided');
      currentCompanyId = `company_${user.uid}`;
    }

    const settings = await getCompanySettings(currentCompanyId);
    if (!settings?.wikiPayitSettings) {
      throw new Error('NMI settings not configured');
    }

    // Use the backend API URL from config
    const apiUrl = getPaymentApiUrl('paymentProcess');
    
    // Prepare request body - process payment without vault operations
    const requestBody = {
      companyId: currentCompanyId,
      payment_token: paymentToken,
      type: 'sale',
      amount: params.amount.toFixed(2),
      orderid: params.orderId,
      customerId: params.customerId,
      orderdescription: params.orderDescription || 'Payment',
      invoiceId: params.invoiceId,
      // Customer data sent to NMI API
      firstname: params.firstName || customerData.firstName,
      lastname: params.lastName || customerData.lastName,
      email: params.email || customerData.email,
      address1: params.address || customerData.address,
      city: params.city || customerData.city,
      state: params.state || customerData.state,
      zip: params.zip || customerData.zip,
    };
    
    console.log('Payment request submitted to:', apiUrl);
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify(requestBody)
    });

    // Handle error responses from the API
    if (!response.ok) {
      try {
        const errorData = await response.json();
        throw new PaymentError(
          errorData.error || 'Payment processing failed',
          errorData.code || 'PROCESSING_ERROR'
        );
      } catch (parseError) {
        if (parseError instanceof PaymentError) {
          throw parseError;
        }
        throw new PaymentError(
          'Error parsing API response',
          'PROCESSING_ERROR'
        );
      }
    }

    const data = await response.json();
    console.log('Payment response received:', { 
      success: data.success, 
      transactionId: data.transactionid,
      responseCode: data.response_code
    });

    return {
      success: data.success,
      transactionid: data.transactionid || data.transaction_id,
      authcode: data.authcode,
      responsetext: data.responsetext,
      response_code: data.response_code,
      cardLast4: data.cardLast4 || cardData.last4,
      cardType: data.cardType || cardData.cardType,
      expiryMonth: data.expiryMonth || cardData.expiryMonth,
      expiryYear: data.expiryYear || cardData.expiryYear,
      customerVaultId: data.customerVaultId || data.customer_vault_id
    };
  } catch (error) {
    if (error instanceof PaymentError) {
      throw error;
    }
    throw new PaymentError(
      error instanceof Error ? error.message : 'Payment processing failed',
      'PROCESSING_ERROR'
    );
  }
};

// Function to process refund
export const processRefund = async (transactionId: string): Promise<NMIResponse> => {
  try {
    const user = auth.currentUser;
    if (!user?.uid) throw new Error('User not authenticated');

    const settings = await getCompanySettings(`company_${user.uid}`);
    if (!settings?.wikiPayitSettings) {
      throw new Error('NMI settings not configured');
    }

    // Use the backend API URL from config
    const apiUrl = getPaymentApiUrl('paymentProcess');
    
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify({
        companyId: `company_${user.uid}`,
        type: 'refund',
        transactionid: transactionId
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new PaymentError(
        errorData.error || 'Refund failed',
        errorData.code || 'REFUND_FAILED'
      );
    }

    const data = await response.json();
    return {
      success: data.success,
      transactionid: data.transactionid,
      responsetext: data.responsetext,
      response_code: data.response_code,
      cardType: data.cardType,
      expiryMonth: data.expiryMonth,
      expiryYear: data.expiryYear,
      customerVaultId: data.customerVaultId || data.customer_vault_id
    };
  } catch (error) {
    console.error('Refund processing error:', error);
    if (error instanceof PaymentError) {
      throw error;
    }
    throw new PaymentError(
      error instanceof Error ? error.message : 'Refund processing failed',
      'REFUND_FAILED'
    );
  }
};

export function getErrorMessage(error: PaymentError): string {
  const errorMessages: Record<string, string> = {
    '200': 'Invalid credit card number',
    '201': 'Invalid expiration date',
    '202': 'Invalid CVV code',
    '203': 'Invalid amount',
    '204': 'Invalid merchant credentials',
    '220': 'Invalid transaction type',
    '221': 'Invalid payment data',
    '222': 'Invalid billing information',
    '223': 'Invalid shipping information',
    '224': 'Invalid transaction data',
    '225': 'Invalid credentials',
    '240': 'Card type not accepted',
    '250': 'Transaction declined',
    '251': 'Duplicate transaction',
    '252': 'Transaction already voided',
    '253': 'Transaction already refunded',
    '260': 'Declined by processor',
    'NETWORK_ERROR': 'Network communication error',
    'UNKNOWN_ERROR': 'An unexpected error occurred',
  };

  return errorMessages[error.code] || error.message;
}

export const getUserFriendlyErrorMessage = (code: string): string => {
  const errorMessages: Record<string, string> = {
    // Transaction Status
    '1': 'Your payment was approved successfully.',
    '2': 'We\'re unable to process your payment at this time. Please contact your card issuer for more information or try a different payment method.',
    '3': 'There was an issue processing your payment. Our team has been notified.',

    // Specific Error Codes - Enhanced with user-friendly, action-oriented messages
    '100': 'Your payment was approved successfully. Thank you!',
    '200': 'The card number you entered appears to be invalid. Please double-check the number and try again.',
    '201': 'Your card was declined by your bank. This could be due to insufficient funds or for security reasons. Please try another card or contact your bank for assistance.',
    '202': 'Your card has insufficient funds. Please use a different card or contact your bank.',
    '203': 'You have reached your card\'s spending limit. Please use a different card or contact your bank to increase your limit.',
    '204': 'This type of transaction is not permitted with this card. Please try a different card or payment method.',
    '220': 'The payment information provided is incomplete or incorrect. Please check all fields and try again.',
    '221': 'We couldn\'t recognize the card issuer. Please try a different card.',
    '222': 'The card number provided doesn\'t match any on file with the issuer. Please verify the number or use a different card.',
    '223': 'This card has expired. Please use a different card with a valid expiration date.',
    '224': 'The expiration date format is invalid. Please use the MM/YY format and try again.',
    '225': 'The security code (CVV) is incorrect. Please check the 3 or 4 digit code on the back of your card.',
    '240': 'This transaction requires additional verification from your bank. Please contact your card issuer.',
    '250': 'Your card has been flagged by your bank. Please contact your bank for more information.',
    '251': 'This card has been reported lost. Please use a different payment method.',
    '252': 'This card has been reported stolen. Please use a different payment method.',
    '253': 'This transaction was flagged as potentially fraudulent. For your security, please use a different payment method or contact your bank.',
    '260': 'Your bank has declined this transaction. This may be due to unusual activity. Please contact your bank or try another payment method.',
    '261': 'This recurring payment has been canceled. Please update your payment information to continue service.',
    '262': 'This recurring payment program has been discontinued. Please set up a new payment method.',
    '263': 'Your card information needs to be updated. Please provide current card details.',
    '264': 'This transaction has been temporarily blocked. Please try again in 24-48 hours or use another payment method.',
    '300': 'There was a temporary issue with our payment system. Please try again in a few minutes.',
    '400': 'The payment couldn\'t be processed due to a technical issue. Please try again or use another payment method.',
    '410': 'This merchant account is incorrectly configured. Our team has been notified of this issue.',
    '411': 'The merchant account is currently unavailable. Our team is working to resolve this issue.',
    '420': 'There was a communication error with the payment processor. Please try again in a few minutes.',
    '421': 'We couldn\'t connect to your bank\'s system. Please try again or use another payment method.',
    '430': 'This appears to be a duplicate payment. Please check if your previous payment was successful before trying again.',
    '440': 'There was an error with how the transaction was formatted. Please try again.',
    '441': 'Some payment information is missing or invalid. Please review all fields and try again.',
    '460': 'This payment method is not supported at this time. Please use a different payment option.',
    '461': 'This type of card is not accepted. Please use a supported card type (Visa, Mastercard, etc.).',
    
    // AVS Response Codes
    'AVS_A': 'Your billing address doesn\'t match the address on file with your bank. Please verify your billing information.',
    'AVS_B': 'The zip code and street address you provided couldn\'t be checked. Please verify your billing information.',
    'AVS_C': 'Your billing information couldn\'t be verified. Please check your address and try again.',
    'AVS_D': 'The zip code and street address match. Thank you!',
    'AVS_E': 'Your billing information couldn\'t be verified at this time. Please try again later.',
    'AVS_F': 'The street address and postal code both match. Thank you!',
    'AVS_G': 'This card was issued by a non-U.S. bank and address verification is not available. Please continue or try another card.',
    'AVS_I': 'Address information wasn\'t checked. Your payment is being processed.',
    'AVS_M': 'Your street address and postal code both match. Thank you!',
    'AVS_N': 'Neither the street address nor postal code match the information on file with your bank. Please check your billing information.',
    'AVS_P': 'Your postal code matches but the street address couldn\'t be verified. You may continue or check your billing address.',
    'AVS_R': 'Your payment is being processed, but your bank\'s system is currently unavailable. Please wait a moment.',
    'AVS_S': 'This service is not supported by your card issuer. Please try another card.',
    'AVS_U': 'Address information is unavailable for this card. You may continue.',
    'AVS_W': 'Your postal code matches but the street address doesn\'t match your bank\'s records. Please check your billing address.',
    'AVS_X': 'Both street address and postal code match. Thank you!',
    'AVS_Y': 'Your address and 5-digit zip code match. Thank you!',
    'AVS_Z': 'Your zip code matches but the street address doesn\'t match your bank\'s records. Please check your billing address.',
    
    // CVV Response Codes
    'CVV_M': 'CVV code match. Thank you!',
    'CVV_N': 'The CVV code you entered doesn\'t match the code on file. Please check and try again.',
    'CVV_P': 'CVV code wasn\'t processed. Please try again.',
    'CVV_S': 'CVV check isn\'t available for this card. You may continue.',
    'CVV_U': 'CVV check couldn\'t be performed. You may continue or try another card.',
    
    // Generic/Default Messages
    'NETWORK_ERROR': 'We couldn\'t connect to the payment system. Please check your internet connection and try again.',
    'PROCESSING_ERROR': 'There was an error processing your payment. Please try again or contact customer support if the issue persists.',
    'INVALID_RESPONSE': 'We received an unexpected response from the payment system. Please try again in a few minutes.',
    'UNKNOWN_ERROR': 'Something went wrong with your payment. Please try again or contact our support team for assistance.'
  };

  // Process the code to check if it's potentially an AVS or CVV response code
  // NMI typically returns these as part of the response object
  if (code === 'M' || code === 'N' || code === 'P' || code === 'S' || code === 'U') {
    // First check if this might be a CVV response code
    const cvvMessage = errorMessages[`CVV_${code}`];
    if (cvvMessage) return cvvMessage;
  }
  
  if (code.length === 1) {
    // Check if this might be an AVS response code
    const avsMessage = errorMessages[`AVS_${code}`];
    if (avsMessage) return avsMessage;
  }

  return errorMessages[code] || 'Your payment could not be processed at this time. Please verify your payment information or try a different payment method.';
};

/**
 * Get detailed error information for display to users
 */
export function getErrorDetails(error: PaymentError): {
  title: string;
  message: string;
  suggestion?: string;
} {
  // Get the user-friendly message based on the error code
  const userFriendlyMessage = getUserFriendlyErrorMessage(error.code);
  
  // Determine the title based on the error code
  let title = 'Payment Error';
  
  // CVV errors
  if (error.code.startsWith('CVV_')) {
    if (error.code === 'CVV_N') {
      title = 'Incorrect Security Code';
    } else {
      title = 'Security Code Issue';
    }
  }
  // AVS errors (address/zip code issues)
  else if (error.code.startsWith('AVS_')) {
    if (['AVS_N', 'AVS_A', 'AVS_Z', 'AVS_W'].includes(error.code)) {
      title = 'Billing Address Mismatch';
    } else {
      title = 'Address Verification';
    }
  }
  // Common decline codes
  else if (error.code === '2') {
    title = 'Transaction Declined';
  }
  else if (error.code === '200') {
    title = 'Invalid Card Number';
  }
  else if (error.code === '201') {
    title = 'Card Declined';
  }
  else if (error.code === '202') {
    title = 'Insufficient Funds';
  }
  else if (error.code === '225') {
    title = 'Invalid Security Code';
  }
  else if (error.code === '223') {
    title = 'Expired Card';
  }
  else if (error.code === '224') {
    title = 'Invalid Expiration Date';
  }
  else if (['250', '260'].includes(error.code)) {
    title = 'Payment Declined';
  }
  else if (error.code === '251') {
    title = 'Duplicate Transaction';
  }
  else if (error.code === 'NETWORK_ERROR') {
    title = 'Connection Problem';
  }
  else if (error.code === 'PROCESSING_ERROR') {
    title = 'Processing Error';
  }
  
  // Determine suggestions based on the error type
  let suggestion: string | undefined;
  
  if (error.code.startsWith('CVV_')) {
    suggestion = 'Please check the 3 or 4 digit security code on your card and try again.';
  } else if (error.code.startsWith('AVS_') && ['AVS_N', 'AVS_A', 'AVS_Z', 'AVS_W'].includes(error.code)) {
    suggestion = 'Please verify your billing address matches the address on file with your bank.';
  } else if (['200', '225'].includes(error.code)) {
    suggestion = 'Please double-check your card information and try again.';
  } else if (error.code === '2') {
    suggestion = 'Your card issuer has declined this transaction. Please contact them directly for specific details about why this payment was not approved.';
  } else if (['201', '202', '260'].includes(error.code)) {
    suggestion = 'Please try a different payment method or contact your bank.';
  } else if (error.code === '223') {
    suggestion = 'Please use a card with a valid expiration date.';
  } else if (error.code === '251') {
    suggestion = 'Please check if your previous payment was already successful.';
  } else if (error.code === 'NETWORK_ERROR') {
    suggestion = 'Please check your internet connection and try again.';
  }
  
  return {
    title: title,
    message: userFriendlyMessage,
    suggestion: suggestion
  };
}
